/**
 * Shared recipe-metaobject search for the predictive header search and the
 * full Shopify search page. The index is rendered by a lightweight section
 * and fetched once per page view, including any additional Liquid pages.
 */
(function initRecipeSearch(global) {
  'use strict';

  const INDEX_SECTION_ID = 'helper-recipe-search-index';
  const MAX_INDEX_PAGES = 10;
  let recipeIndexPromise;

  function normalizeSearchText(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function recipeScore(recipe, normalizedQuery, terms) {
    const title = normalizeSearchText(recipe.title);

    if (title === normalizedQuery) return 0;
    if (title.startsWith(normalizedQuery)) return 1;
    if (title.includes(normalizedQuery)) return 2;
    if (terms.every((term) => title.includes(term))) return 3;
    return 4;
  }

  function searchRecipes(recipes, query, limit) {
    const normalizedQuery = normalizeSearchText(query);
    const terms = normalizedQuery.split(' ').filter(Boolean);

    if (!normalizedQuery || terms.length === 0) return [];

    const results = (recipes || [])
      .filter((recipe) => {
        const searchableText = normalizeSearchText(recipe.searchText || recipe.title);
        return terms.every((term) => searchableText.includes(term));
      })
      .sort((recipeA, recipeB) => {
        const scoreDifference = recipeScore(recipeA, normalizedQuery, terms) - recipeScore(recipeB, normalizedQuery, terms);
        if (scoreDifference !== 0) return scoreDifference;
        return String(recipeA.title).localeCompare(String(recipeB.title));
      });

    return Number.isInteger(limit) ? results.slice(0, limit) : results;
  }

  function indexBaseUrl() {
    return global.KROWN?.settings?.routes?.recipe_search_index_url || '/pages/recipes';
  }

  async function loadRecipeIndex() {
    if (recipeIndexPromise) return recipeIndexPromise;

    recipeIndexPromise = (async () => {
      const recipes = [];
      const seenUrls = new Set();
      let nextUrl = indexBaseUrl();
      let pageCount = 0;

      while (nextUrl && pageCount < MAX_INDEX_PAGES) {
        const url = new URL(nextUrl, global.location.origin);
        url.searchParams.set('section_id', INDEX_SECTION_ID);

        const response = await global.fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Recipe search index request returned ${response.status}`);

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const indexNode = documentFragment.querySelector('[data-recipe-search-index]');
        if (!indexNode) throw new Error('Recipe search index markup was not found');

        const batch = JSON.parse(indexNode.textContent || '[]');
        batch.forEach((recipe) => {
          if (!recipe?.title || !recipe?.url || seenUrls.has(recipe.url)) return;
          seenUrls.add(recipe.url);
          recipes.push(recipe);
        });

        nextUrl = indexNode.dataset.nextUrl || '';
        pageCount += 1;
      }

      return recipes;
    })().catch((error) => {
      recipeIndexPromise = null;
      throw error;
    });

    return recipeIndexPromise;
  }

  async function find(query, limit) {
    return searchRecipes(await loadRecipeIndex(), query, limit);
  }

  function createRecipeLink(documentNode, recipe, predictive = false) {
    const item = predictive ? documentNode.createElement('a') : documentNode.createElement('div');
    const link = predictive ? item : documentNode.createElement('a');
    const outerText = documentNode.createElement('span');
    const innerText = documentNode.createElement('span');

    item.className = 'search-item';
    link.href = recipe.url;
    link.title = recipe.title;
    outerText.className = predictive ? 'text-weight--bold' : 'text-animation--underline text-weight--bold';
    innerText.className = predictive ? 'text-animation--underline-thin' : '';
    innerText.textContent = recipe.title;
    outerText.appendChild(innerText);
    link.appendChild(outerText);

    if (predictive) {
      link.dataset.jsSearchItem = '';
      link.tabIndex = -1;
    } else {
      item.appendChild(link);
    }

    return item;
  }

  function appendPredictiveResults(root, recipes, label = 'Recipes') {
    const holder = root?.querySelector('[data-js-search-results-holder]');
    if (!holder) return;

    holder.querySelector('[data-recipe-search-results]')?.remove();
    if (!recipes.length) return;

    const block = holder.ownerDocument.createElement('div');
    const title = holder.ownerDocument.createElement('span');
    block.className = 'search-block';
    block.dataset.recipeSearchResults = '';
    block.dataset.searchResultType = 'recipes';
    title.className = 'search-title';
    title.textContent = label;
    block.appendChild(title);
    recipes.forEach((recipe) => block.appendChild(createRecipeLink(holder.ownerDocument, recipe, true)));

    const followingBlock = holder.querySelector('[data-search-result-type="products"], [data-search-result-type="pages"], [data-search-result-type="articles"]');
    holder.insertBefore(block, followingBlock || null);
  }

  function renderSearchPageResults(element, recipes) {
    if (!recipes.length) return;

    const list = element.querySelector('[data-recipe-search-list]');
    const count = element.querySelector('[data-recipe-search-count]');
    if (!list || !count) return;

    list.replaceChildren(...recipes.map((recipe) => createRecipeLink(element.ownerDocument, recipe)));
    count.textContent = String(recipes.length);
    element.hidden = false;

    const searchRoot = element.closest('.main-search') || element.ownerDocument;
    const emptyMessage = searchRoot.querySelector('[data-search-empty-message]');
    const nativeSummary = searchRoot.querySelector('[data-search-results-summary][data-native-results-count="0"]');
    if (emptyMessage) emptyMessage.hidden = true;
    if (nativeSummary) nativeSummary.hidden = true;
  }

  class RecipeSearchResults extends (global.HTMLElement || class {}) {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        renderSearchPageResults(this, await find(this.dataset.query || ''));
      } catch (error) {
        console.warn('Could not load recipe search results.', error);
      }
    }
  }

  const api = {
    appendPredictiveResults,
    find,
    loadRecipeIndex,
    normalizeSearchText,
    searchRecipes,
  };

  global.BffRecipeSearch = api;

  if (global.customElements && !global.customElements.get('recipe-search-results')) {
    global.customElements.define('recipe-search-results', RecipeSearchResults);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { normalizeSearchText, searchRecipes };
  }
})(typeof window !== 'undefined' ? window : globalThis);
